<template>
  <PreviewFrame :accent="slide.accent">
    <template #presenter>
      <div class="tr-header">
        <span class="tr-category">SCIENCE • 200</span>
        <div class="tr-timer">
          <svg viewBox="0 0 36 36" class="tr-timer-svg">
            <circle
              cx="18"
              cy="18"
              r="15"
              class="tr-timer-track"
            />
            <circle
              cx="18"
              cy="18"
              r="15"
              class="tr-timer-progress"
            />
          </svg>
          <span class="tr-timer-num">8</span>
        </div>
      </div>
      <div class="tr-question">
        <span class="tr-bar tr-bar--xl"></span>
        <span class="tr-bar tr-bar--lg"></span>
      </div>
      <div class="tr-options">
        <div
          v-for="(o, i) in options"
          :key="o.letter"
          class="tr-option"
          :style="{ '--opt-i': i }"
        >
          <span class="tr-opt-letter">{{ o.letter }}</span>
          <span class="tr-bar tr-bar--md"></span>
        </div>
      </div>
    </template>

    <template #audience>
      <div class="tr-phone-cat">
        SCIENCE
      </div>
      <button
        v-for="(o, i) in options"
        :key="'p' + o.letter"
        type="button"
        class="tr-phone-opt"
        :class="{ '--tapped': i === 2 }"
        :style="{ '--opt-i': i }"
        tabindex="-1"
      >
        <span class="tr-opt-letter">{{ o.letter }}</span>
        <span class="tr-bar tr-bar--md"></span>
      </button>
    </template>
  </PreviewFrame>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import type { SlideType } from '../../data/slideTypeCatalog'
import PreviewFrame from './PreviewFrame.vue'

export default Vue.extend({
  name: 'PreviewTrivia',
  components: { PreviewFrame },
  props: {
    slide: { type: Object as PropType<SlideType>, required: true },
  },
  data() {
    return {
      options: [
        { letter: 'A' }, { letter: 'B' }, { letter: 'C' }, { letter: 'D' },
      ],
    }
  },
})
</script>

<style scoped lang="scss">
.tr-bar {
  display: block; height: 7px; border-radius: 999px;
  background: rgba(15, 15, 20, 0.1);
}
.tr-bar--xl { width: 80%; height: 8px; background: rgba(15, 15, 20, 0.2); }
.tr-bar--lg { width: 55%; }
.tr-bar--md { width: 60%; }

.tr-header {
  display: flex; align-items: center; justify-content: space-between;
  gap: 10px;
}
.tr-category {
  font-size: 10px; font-weight: 800;
  letter-spacing: 0.1em;
  color: var(--accent);
  background: color-mix(in srgb, var(--accent) 10%, white);
  padding: 4px 10px;
  border-radius: 999px;
}
.tr-timer {
  position: relative;
  width: 32px; height: 32px;
}
.tr-timer-svg {
  width: 100%; height: 100%;
  transform: rotate(-90deg);
}
.tr-timer-track {
  fill: none; stroke: rgba(15, 15, 20, 0.08);
  stroke-width: 3;
}
.tr-timer-progress {
  fill: none; stroke: var(--accent);
  stroke-width: 3;
  stroke-linecap: round;
  stroke-dasharray: 94.25;
  stroke-dashoffset: 0;
  animation: tr-tick 4s linear infinite;
}
.tr-timer-num {
  position: absolute; inset: 0;
  display: grid; place-items: center;
  font-size: 11px; font-weight: 800;
  color: var(--accent);
}

.tr-question {
  display: flex; flex-direction: column; gap: 5px;
  padding: 4px 0;
}

.tr-options {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 6px;
  margin-top: auto;
}
.tr-option {
  display: grid;
  grid-template-columns: 18px 1fr;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  background: white;
  border: 1px solid rgba(15, 15, 20, 0.08);
  border-radius: 6px;
}
.tr-opt-letter {
  width: 18px; height: 18px;
  border-radius: 999px;
  background: rgba(15, 15, 20, 0.06);
  color: rgba(15, 15, 20, 0.55);
  font-size: 10px; font-weight: 700;
  display: grid; place-items: center;
}

/* ---- Audience phone ---- */
.tr-phone-cat {
  font-size: 9px; font-weight: 800;
  letter-spacing: 0.1em;
  color: white;
  background: var(--accent);
  padding: 4px 8px;
  border-radius: 999px;
  align-self: flex-start;
  margin-bottom: 4px;
}
.tr-phone-opt {
  appearance: none; background: white;
  display: grid;
  grid-template-columns: 18px 1fr;
  align-items: center;
  gap: 8px;
  padding: 7px 9px;
  border-radius: 8px;
  border: 1px solid rgba(15, 15, 20, 0.08);
  cursor: pointer;
  font-family: inherit;
  text-align: left;
}
.tr-phone-opt.--tapped {
  border-color: color-mix(in srgb, var(--accent) 60%, transparent);
  background: color-mix(in srgb, var(--accent) 10%, white);
  animation: tr-tap 4s ease-out infinite;
}
.tr-phone-opt.--tapped .tr-opt-letter { background: var(--accent); color: white; }

@keyframes tr-tick {
  0%   { stroke-dashoffset: 0; }
  100% { stroke-dashoffset: 94.25; }
}
@keyframes tr-tap {
  0%, 4%   { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 35%, transparent); }
  18%      { box-shadow: 0 0 0 8px color-mix(in srgb, var(--accent) 0%, transparent); }
  100%     { box-shadow: 0 0 0 0 color-mix(in srgb, var(--accent) 0%, transparent); }
}

@media (prefers-reduced-motion: reduce) {
  .tr-timer-progress, .tr-phone-opt.--tapped { animation: none; }
}
</style>
